///////////////////////////////////////////////////////////////////////////////////
//
// "הוספה למסך הבית" - PlanUp as an app on the phone's / tablet's home screen.
//
// The pieces: public/manifest.json (name, icons, opens /dashboard full screen), the icons in
// public/static/pwa (scripts/pwa-icons/make-icons.py), public/sw.js (the service worker Chrome
// wants before it offers its one-tap install - no caching) and two places that offer it:
// HomeScreenPrompt.vue, the small popup after login, and HomeScreenSettings.vue in /settings.
//
// How a device adds it:
//   - Chrome / Edge / Samsung on Android (and Chrome / Edge on a computer) hand the page a
//     beforeinstallprompt event once the site qualifies; promptInstall() opens their install
//     dialog from our button. Until it comes (or in a browser that never sends it) the popup
//     explains the browser's menu instead.
//   - iPhone / iPad have no such event: the popup shows the steps (share button → "הוספה למסך
//     הבית").
//
// What the device decided is kept in its own localStorage (planupHomeScreen) - adding to the home
// screen is per device, so is "not now" (SNOOZE_DAYS) and "don't show again" (the settings
// section turns the offer back on).
//
///////////////////////////////////////////////////////////////////////////////////

import Vue from 'vue';


const STORAGE_KEY = 'planupHomeScreen';

const SNOOZE_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;


function readPrefs() {

    try {
        let raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
        return {
            never: !!raw.never,
            snoozedUntil: +raw.snoozedUntil || 0,
            installedAt: +raw.installedAt || 0,
        };
    } catch (e) {
        return { never: false, snoozedUntil: 0, installedAt: 0 };
    }

}


//running from the home screen icon right now (Android: any installed display mode; iOS: navigator.standalone)
export function isStandalone() {

    if (window.navigator.standalone === true)
        return true;

    if (!window.matchMedia)
        return false;

    return ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay'].some(function (mode) {
        return window.matchMedia('(display-mode: ' + mode + ')').matches;
    });

}


//'ios' | 'android' | 'desktop'. iPadOS 13+ says "Macintosh" - a Mac with a touch screen is an iPad
export function devicePlatform() {

    let ua = navigator.userAgent || '';

    if (/iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
        return 'ios';

    if (/Android/i.test(ua))
        return 'android';

    return 'desktop';

}


//iOS: Safari, or another browser (Chrome / Firefox / Edge add from the share button in their address bar)
export function iosBrowserIsSafari() {
    return !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|YaBrowser|DuckDuckGo/i.test(navigator.userAgent || '');
}


//what the popup and the settings section read - reactive, the browser's events change it
export const homeScreenState = Vue.observable({
    //the browser handed us its install dialog (beforeinstallprompt)
    canPrompt: false,
    standalone: false,
    prefs: { never: false, snoozedUntil: 0, installedAt: 0 },
});


function savePrefs() {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(homeScreenState.prefs));
    } catch (e) {
        //storage disabled: the choice holds for this page only
    }
}


let deferredPrompt = null;


if (typeof window !== 'undefined') {

    homeScreenState.prefs = readPrefs();
    homeScreenState.standalone = isStandalone();

    //opened from the icon: this device has it (on Android the browser shares this storage, so its
    //tabs stop offering it too)
    if (homeScreenState.standalone && !homeScreenState.prefs.installedAt)
        markInstalled();

    window.addEventListener('beforeinstallprompt', function (event) {
        //no browser mini-infobar - the popup asks at a quiet moment, after login
        event.preventDefault();
        deferredPrompt = event;
        homeScreenState.canPrompt = true;
    });

    //added from our button or from the browser's own menu
    window.addEventListener('appinstalled', function () {
        deferredPrompt = null;
        homeScreenState.canPrompt = false;
        markInstalled();
    });

}


export function isInstalled() {
    return homeScreenState.standalone || !!homeScreenState.prefs.installedAt;
}


//the popup after login: phones and tablets that don't have the app yet and didn't say no
export function shouldOfferAfterLogin() {

    let prefs = homeScreenState.prefs;

    if (isInstalled() || prefs.never)
        return false;

    if (prefs.snoozedUntil && prefs.snoozedUntil > Date.now())
        return false;

    return devicePlatform() != 'desktop';

}


export function offerAfterLoginIsOn() {
    return !homeScreenState.prefs.never;
}


//"לא עכשיו" - asked again in a week
export function snooze() {
    homeScreenState.prefs = Object.assign({}, homeScreenState.prefs, { snoozedUntil: Date.now() + SNOOZE_DAYS * DAY_MS });
    savePrefs();
}


//"לא להציג שוב" (off) / the settings checkbox (on: offered again from the next login)
export function setOfferAfterLogin(on) {
    homeScreenState.prefs = Object.assign({}, homeScreenState.prefs, on ? { never: false, snoozedUntil: 0 } : { never: true });
    savePrefs();
}


export function markInstalled() {
    homeScreenState.prefs = Object.assign({}, homeScreenState.prefs, { installedAt: Date.now() });
    savePrefs();
}


//the browser's own install dialog. Call it straight from a click (the browser wants a user
//gesture). Resolves 'accepted' | 'dismissed' | 'unavailable' (no dialog to show - explain instead)
export function promptInstall() {

    let event = deferredPrompt;

    if (!event)
        return Promise.resolve('unavailable');

    //a dialog can be shown once; the browser sends a fresh event when it may be shown again
    deferredPrompt = null;
    homeScreenState.canPrompt = false;

    let shown;

    try {
        shown = event.prompt();
    } catch (e) {
        return Promise.resolve('unavailable');
    }

    return Promise.resolve(shown)
        .then(function () { return event.userChoice; })
        .then(function (choice) {
            if (choice && choice.outcome == 'accepted') {
                markInstalled();
                return 'accepted';
            }
            return 'dismissed';
        })
        .catch(function () { return 'unavailable'; });

}


let workerRegistered = false;

//public/sw.js - registered once the user is logged in (the landing page's visitors don't get it)
export function registerServiceWorker() {

    if (workerRegistered || !('serviceWorker' in navigator))
        return;

    let local = location.hostname == 'localhost' || location.hostname == '127.0.0.1';

    if (location.protocol != 'https:' && !local)
        return;

    workerRegistered = true;

    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(function (e) {
        console.log('service worker not registered:', e && e.message);
    });

}
